import { z } from 'zod';
import { baseModelSchema } from '../../../model/baseModel';
import { IPackLineFormValues } from './packLineFormValues';

/**
 * Zod schema for validating pack line data.
 * Extends the base model schema with pack line-specific fields.
 *
 * @property {string} code - Unique identifier code (max 5 characters)
 * @property {string} name - Name of the pack line (max 255 characters)
 * @property {number} siteId - ID of the site this pack line belongs to
 */
export const packLineSchema = baseModelSchema.extend({
    code: z.string().nonempty('Code is required').max(5, 'Code can only be up to 5 characters long.').default(''),
    name: z.string().nonempty('Name is required').max(255, 'Name can only be up to 255 characters long.').default(''),
    siteId: z.number(),
});

/**
 * TypeScript interface representing a Pack Line object.
 * Generated from the Zod schema for type safety.
 */
export type IPackLine = z.infer<typeof packLineSchema>;

/**
 * Creates a validated Pack Line object from form values.
 *
 * @param {IPackLineFormValues} formValues - The form data to convert
 * @returns {IPackLine} A validated pack line object
 * @throws {ZodError} If validation fails
 */
export function PackLine(formValues : IPackLineFormValues) : IPackLine {
    const { site } = formValues;
    return packLineSchema.parse({
        ...formValues,
        siteId: site?.value ? Number(site.value) : undefined,
    });
};
